import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Clock, Loader2, Shield, Trash2, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBeliefGraph } from '../hooks/useBeliefGraph';
import { useExtractionStatus } from '../hooks/useExtractionStatus';
import { pruneNode } from '../services/beliefGraph';
import { createSuppressionRule } from '../services/memory';
import { getSession } from '../services/sessions';
import { getSessionProcessingCopy, getSessionProcessingLine } from '../utils/sessionProcessingCopy';
import { getConversationSessionDate, getConversationSessionSummary, getConversationSessionTitle } from '../components/session/ConversationSessionCard';
import type { BeliefNode, BeliefTier, FlankTimestamp, Session } from '../types';

const TIER_CONFIG: Record<BeliefTier, { label: string; icon: typeof Shield }> = {
  axiomatic: { label: 'Core beliefs', icon: Shield },
  active: { label: 'Active beliefs', icon: Zap },
  situational: { label: 'Situational beliefs', icon: Clock },
};

type BeliefRange = 'today' | 'all';
type BeliefSort = 'mentions' | 'confidence' | 'recent' | 'alphabetical';

export default function InsightsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { nodesByTier, edges, motifs, loading } = useBeliefGraph(user?.uid ?? null);
  const { isExtracting, extractingSessionType, extractingSessionId } = useExtractionStatus(user?.uid ?? null);
  const processingCopy = getSessionProcessingCopy(extractingSessionType);
  const processingLine = getSessionProcessingLine(extractingSessionType, extractingSessionId);

  const [expandedTiers, setExpandedTiers] = useState<Record<BeliefTier, boolean>>({
    axiomatic: false,
    active: false,
    situational: false,
  });
  const [pendingRemoval, setPendingRemoval] = useState<{ nodeId: string; label: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selectedRange: BeliefRange = searchParams.get('range') === 'today' ? 'today' : 'all';
  const selectedSort: BeliefSort = (
    searchParams.get('sort') === 'confidence'
    || searchParams.get('sort') === 'recent'
    || searchParams.get('sort') === 'alphabetical'
  )
    ? (searchParams.get('sort') as BeliefSort)
    : 'mentions';
  const todayRange = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }, []);
  const filteredNodesByTier = useMemo(() => ({
    axiomatic: sortBeliefs(nodesByTier.axiomatic.filter((node) => (
      selectedRange === 'all' || isTimestampInRange(node.createdAt, todayRange.start, todayRange.end)
    )), selectedSort),
    active: sortBeliefs(nodesByTier.active.filter((node) => (
      selectedRange === 'all' || isTimestampInRange(node.createdAt, todayRange.start, todayRange.end)
    )), selectedSort),
    situational: sortBeliefs(nodesByTier.situational.filter((node) => (
      selectedRange === 'all' || isTimestampInRange(node.createdAt, todayRange.start, todayRange.end)
    )), selectedSort),
  }), [nodesByTier, selectedRange, selectedSort, todayRange.end, todayRange.start]);

  const setRange = (range: BeliefRange) => {
    const next = new URLSearchParams(searchParams);
    next.set('range', range);
    next.set('sort', selectedSort);
    setSearchParams(next, { replace: true });
  };

  const setSort = (sort: BeliefSort) => {
    const next = new URLSearchParams(searchParams);
    next.set('range', selectedRange);
    next.set('sort', sort);
    setSearchParams(next, { replace: true });
  };

  async function handleDelete(withSuppression: boolean) {
    if (!user || !pendingRemoval) return;
    setRemoving(true);
    setFeedback(null);
    setError(null);
    try {
      await pruneNode(user.uid, pendingRemoval.nodeId);
      if (withSuppression) {
        await createSuppressionRule(user.uid, pendingRemoval.label, 'belief');
      }
      setFeedback(
        withSuppression
          ? `Deleted "${pendingRemoval.label}" and added to suppressed concepts.`
          : `Deleted "${pendingRemoval.label}".`
      );
      setPendingRemoval(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete belief');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="sticky top-0 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)] px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings?tab=insights')}
          className="p-2 -ml-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-semibold text-[var(--color-text-primary)]">Beliefs</h1>
        <button
          onClick={() => navigate(`/insights/memories?range=${selectedRange}`)}
          className="ml-auto text-xs px-2 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]"
        >
          Memories
        </button>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col gap-4">
        {isExtracting && (
          <div className="flank-card rounded-xl px-4 py-3 flex items-center gap-2">
            <Loader2 size={15} className="animate-spin text-[var(--color-brand-primary)]" />
            <div>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">{processingCopy.title}</p>
              <p className="text-xs text-[var(--color-text-secondary)]">{processingLine}</p>
            </div>
          </div>
        )}

        {(feedback || error) && (
          <p className="text-xs" style={{ color: error ? 'var(--color-error)' : 'var(--color-success)' }}>
            {error ?? feedback}
          </p>
        )}

        <div className="flank-card rounded-xl p-3 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded-lg border border-[var(--color-border)] p-0.5">
            <button
              onClick={() => setRange('today')}
              className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
              style={{
                background: selectedRange === 'today' ? 'var(--color-surface-elevated)' : 'transparent',
                color: selectedRange === 'today' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              }}
            >
              Today
            </button>
            <button
              onClick={() => setRange('all')}
              className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
              style={{
                background: selectedRange === 'all' ? 'var(--color-surface-elevated)' : 'transparent',
                color: selectedRange === 'all' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              }}
            >
              All beliefs
            </button>
          </div>

          <label className="inline-flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
            Sort
            <select
              value={selectedSort}
              onChange={(event) => setSort(event.target.value as BeliefSort)}
              className="rounded-md border px-2 py-1 bg-[var(--color-background)] text-[var(--color-text-primary)]"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <option value="mentions">Frequency of mention</option>
              <option value="confidence">Confidence</option>
              <option value="recent">Most recent</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
          </label>
        </div>

        <div className="flank-card rounded-xl p-4 grid grid-cols-5 gap-3 text-center">
          <StatBox label="Core" value={filteredNodesByTier.axiomatic.length} />
          <StatBox label="Active" value={filteredNodesByTier.active.length} />
          <StatBox label="Situational" value={filteredNodesByTier.situational.length} />
          <StatBox label="Links" value={edges.length} />
          <StatBox label="Patterns" value={motifs.length} />
        </div>

        <section className="flank-card rounded-xl p-4">
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 size={18} className="animate-spin text-[var(--color-text-secondary)]" />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {(['axiomatic', 'active', 'situational'] as BeliefTier[]).map((tier) => (
                <TierSection
                  key={tier}
                  tier={tier}
                  nodes={filteredNodesByTier[tier]}
                  userId={user?.uid ?? null}
                  expanded={expandedTiers[tier]}
                  onToggle={() => setExpandedTiers((prev) => ({ ...prev, [tier]: !prev[tier] }))}
                  onRemove={(node) => setPendingRemoval({ nodeId: node.id, label: node.label })}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {pendingRemoval && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-sm rounded-2xl p-5 bg-[var(--color-background)] border border-[var(--color-border)]">
            <h3 className="font-semibold text-[var(--color-text-primary)]">Delete belief?</h3>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              Remove "{pendingRemoval.label}" from your beliefs?
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-2">
              Use "Delete and don&apos;t relearn" to suppress semantically similar future extractions.
            </p>

            <button
              onClick={() => handleDelete(true)}
              disabled={removing}
              className="w-full mt-4 h-9 rounded-lg border text-sm"
              style={{
                borderColor: 'color-mix(in srgb, var(--color-error) 50%, var(--color-border))',
                color: 'var(--color-error)',
              }}
            >
              {removing ? 'Working...' : "Delete and don't relearn"}
            </button>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setPendingRemoval(null)}
                disabled={removing}
                className="h-9 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-secondary)]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(false)}
                disabled={removing}
                className="h-9 rounded-lg text-sm text-white"
                style={{ background: 'var(--color-error)' }}
              >
                {removing ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-xs text-[var(--color-text-muted)]">{label}</p>
      <p className="text-sm font-semibold text-[var(--color-text-primary)] mt-0.5">{value}</p>
    </div>
  );
}

function TierSection({
  tier,
  nodes,
  userId,
  expanded,
  onToggle,
  onRemove,
}: {
  tier: BeliefTier;
  nodes: BeliefNode[];
  userId: string | null;
  expanded: boolean;
  onToggle: () => void;
  onRemove: (node: BeliefNode) => void;
}) {
  const navigate = useNavigate();
  const Icon = TIER_CONFIG[tier].icon;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedSessionRows, setExpandedSessionRows] = useState<Record<string, boolean>>({});
  const [sessionMetaById, setSessionMetaById] = useState<Record<string, LinkedSessionMeta>>({});
  const [loadingSessionRows, setLoadingSessionRows] = useState<Record<string, boolean>>({});
  const [sessionErrorsByRow, setSessionErrorsByRow] = useState<Record<string, string>>({});

  async function loadSessionMetaForRow(node: BeliefNode, sourceSessionIds: string[]) {
    if (!userId || sourceSessionIds.length === 0) return;
    const missingSessionIds = sourceSessionIds.filter((sessionId) => !sessionMetaById[sessionId]);
    if (missingSessionIds.length === 0) return;

    setLoadingSessionRows((prev) => ({ ...prev, [node.id]: true }));
    setSessionErrorsByRow((prev) => ({ ...prev, [node.id]: '' }));

    try {
      const sessions = await Promise.all(
        missingSessionIds.map(async (sessionId) => ({
          sessionId,
          session: await getSession(userId, sessionId),
        }))
      );

      setSessionMetaById((prev) => {
        const next = { ...prev };
        for (const { sessionId, session } of sessions) {
          if (!session) continue;
          next[sessionId] = buildLinkedSessionMeta(session);
        }
        return next;
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not load sessions';
      setSessionErrorsByRow((prev) => ({ ...prev, [node.id]: message }));
    } finally {
      setLoadingSessionRows((prev) => ({ ...prev, [node.id]: false }));
    }
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full min-h-[42px] px-2 rounded-lg flex items-center gap-2 text-left hover:bg-[var(--color-surface)] transition-colors"
      >
        <Icon size={14} className="text-[var(--color-text-muted)]" />
        <p className="text-[13px] uppercase tracking-wider text-[var(--color-text-secondary)]">
          {TIER_CONFIG[tier].label}
        </p>
        <span className="text-xs text-[var(--color-text-muted)]">{nodes.length}</span>
        <ChevronDown
          size={14}
          className="ml-auto text-[var(--color-text-muted)] transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
        />
      </button>
      {expanded && (
        <div className="mt-1">
          {nodes.length === 0 && (
            <p className="text-xs text-[var(--color-text-muted)] px-3 py-2">No items</p>
          )}
          <div className="flex flex-col gap-2.5">
            {nodes.map((node) => {
              const mentionCount = getBeliefMentionCount(node);
              const sourceSessionIds = getBeliefSessionIds(node);
              const isRowExpanded = Boolean(expandedRows[node.id]);
              const isSessionsExpanded = Boolean(expandedSessionRows[node.id]);
              const recentReinforcements = [...(node.reinforcements ?? [])]
                .filter((item) => item.quote?.trim())
                .slice(-3)
                .reverse();

              return (
                <div
                  key={node.id}
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedRows((prev) => ({ ...prev, [node.id]: !prev[node.id] }))}
                    className="w-full px-3 py-3 flex items-start gap-2 text-left hover:bg-[var(--color-surface-elevated)] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--color-text-primary)] truncate">{node.label}</p>
                      <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        {node.type} · {node.domain.join(', ')}
                      </p>
                      <p className="text-[11px] text-[var(--color-text-secondary)] mt-1">
                        {mentionCount} {mentionCount === 1 ? 'mention' : 'mentions'} · {sourceSessionIds.length} {sourceSessionIds.length === 1 ? 'session' : 'sessions'}
                      </p>
                    </div>
                    <ChevronDown
                      size={16}
                      className="mt-0.5 text-[var(--color-text-muted)] transition-transform duration-200"
                      style={{ transform: isRowExpanded ? 'rotate(180deg)' : undefined }}
                    />
                  </button>

                  {isRowExpanded && (
                    <div className="px-3 pb-3 border-t border-[var(--color-border)]">
                      <div className="pt-2 flex justify-end">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            onRemove(node);
                          }}
                          className="p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-surface-elevated)]"
                          aria-label={`Delete ${node.label}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {node.extractedQuote?.trim() && (
                        <p className="text-xs text-[var(--color-text-secondary)] italic">
                          "{node.extractedQuote.trim()}"
                        </p>
                      )}

                      {recentReinforcements.length > 0 && (
                        <div className="mt-2 flex flex-col gap-1.5">
                          {recentReinforcements.map((reinforcement, idx) => (
                            <p key={`${node.id}-${reinforcement.sessionId}-${idx}`} className="text-[11px] text-[var(--color-text-muted)]">
                              • {reinforcement.quote}
                            </p>
                          ))}
                        </div>
                      )}

                      <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)]">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            const nextExpanded = !isSessionsExpanded;
                            setExpandedSessionRows((prev) => ({ ...prev, [node.id]: nextExpanded }));
                            if (nextExpanded) {
                              void loadSessionMetaForRow(node, sourceSessionIds);
                            }
                          }}
                          className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-[var(--color-surface)] transition-colors"
                        >
                          <p className="text-xs font-medium text-[var(--color-text-secondary)]">
                            Sessions ({sourceSessionIds.length})
                          </p>
                          <ChevronDown
                            size={14}
                            className="ml-auto text-[var(--color-text-muted)] transition-transform duration-200"
                            style={{ transform: isSessionsExpanded ? 'rotate(180deg)' : undefined }}
                          />
                        </button>

                        {isSessionsExpanded && (
                          <div className="px-3 pb-3 border-t border-[var(--color-border)]">
                            {sourceSessionIds.length === 0 ? (
                              <p className="pt-2 text-[11px] text-[var(--color-text-muted)]">
                                No linked sessions yet.
                              </p>
                            ) : (
                              <div className="pt-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-2 flex flex-col gap-2">
                                {getSortedLinkedSessions(sourceSessionIds, node, sessionMetaById).map((linkedSession) => (
                                  <button
                                    key={linkedSession.id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(`/conversations/${linkedSession.id}`);
                                    }}
                                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-2 text-left hover:bg-[var(--color-surface)] transition-colors"
                                  >
                                    <p className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                                      {formatLinkedSessionDate(linkedSession.startedAt)}
                                    </p>
                                    <p className="mt-0.5 text-xs font-medium text-[var(--color-text-primary)] line-clamp-1">
                                      {linkedSession.title}
                                    </p>
                                  </button>
                                ))}
                                {loadingSessionRows[node.id] && (
                                  <p className="text-[11px] text-[var(--color-text-muted)]">
                                    Loading session details...
                                  </p>
                                )}
                                {sessionErrorsByRow[node.id] && !loadingSessionRows[node.id] && (
                                  <p className="text-[11px] text-[var(--color-error)]">
                                    {sessionErrorsByRow[node.id]}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function getBeliefMentionCount(node: BeliefNode): number {
  return Math.max(1, node.reinforcements?.length ?? 0);
}

function getBeliefSessionIds(node: BeliefNode): string[] {
  const reinforcementSessions = (node.reinforcements ?? [])
    .map((item) => item.sessionId)
    .filter(Boolean);
  const sourceSessions = node.source ? [node.source] : [];
  return Array.from(new Set([...sourceSessions, ...reinforcementSessions]));
}

function sortBeliefs(nodes: BeliefNode[], sort: BeliefSort): BeliefNode[] {
  return [...nodes].sort((a, b) => {
    if (sort === 'confidence') {
      const confidenceDiff = (b.confidence ?? 0) - (a.confidence ?? 0);
      if (confidenceDiff !== 0) return confidenceDiff;
      const mentionDiff = getBeliefMentionCount(b) - getBeliefMentionCount(a);
      if (mentionDiff !== 0) return mentionDiff;
    } else if (sort === 'recent') {
      const updatedDiff = getTimestampMillis(b.updatedAt) - getTimestampMillis(a.updatedAt);
      if (updatedDiff !== 0) return updatedDiff;
      const mentionDiff = getBeliefMentionCount(b) - getBeliefMentionCount(a);
      if (mentionDiff !== 0) return mentionDiff;
    } else if (sort === 'alphabetical') {
      const labelDiff = a.label.localeCompare(b.label);
      if (labelDiff !== 0) return labelDiff;
      const mentionDiff = getBeliefMentionCount(b) - getBeliefMentionCount(a);
      if (mentionDiff !== 0) return mentionDiff;
    } else {
      const mentionDiff = getBeliefMentionCount(b) - getBeliefMentionCount(a);
      if (mentionDiff !== 0) return mentionDiff;
      const confidenceDiff = (b.confidence ?? 0) - (a.confidence ?? 0);
      if (confidenceDiff !== 0) return confidenceDiff;
    }

    const updatedDiff = getTimestampMillis(b.updatedAt) - getTimestampMillis(a.updatedAt);
    if (updatedDiff !== 0) return updatedDiff;
    return a.label.localeCompare(b.label);
  });
}

function getTimestampMillis(value?: { toMillis?: () => number }): number {
  if (!value?.toMillis) return 0;
  return value.toMillis();
}

function isTimestampInRange(value: FlankTimestamp, start: Date, end: Date): boolean {
  const date = value?.toDate ? value.toDate() : new Date(value.seconds * 1000);
  return date >= start && date < end;
}

interface LinkedSessionMeta {
  id: string;
  startedAt: Date;
  title: string;
}

function buildLinkedSessionMeta(session: Session): LinkedSessionMeta {
  const explicitTitle = session.outputs?.sessionTitle?.trim()
    || session.outputs?.taskTitle?.trim();
  const summaryFallback = getConversationSessionSummary(session)?.trim();

  return {
    id: session.id,
    startedAt: getConversationSessionDate(session),
    title: explicitTitle || summaryFallback || getConversationSessionTitle(session),
  };
}

function getSortedLinkedSessions(
  sourceSessionIds: string[],
  node: BeliefNode,
  sessionMetaById: Record<string, LinkedSessionMeta>
): LinkedSessionMeta[] {
  const fromMeta = sourceSessionIds
    .map((sessionId) => sessionMetaById[sessionId] ?? null)
    .filter((session): session is LinkedSessionMeta => Boolean(session));

  const missing = sourceSessionIds
    .filter((sessionId) => !sessionMetaById[sessionId])
    .map((sessionId) => buildFallbackSessionMeta(node, sessionId));

  return [...fromMeta, ...missing].sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
}

function buildFallbackSessionMeta(node: BeliefNode, sessionId: string): LinkedSessionMeta {
  const reinforcement = [...(node.reinforcements ?? [])]
    .filter((item) => item.sessionId === sessionId)
    .sort((a, b) => getTimestampFromFlankValue(b.timestamp).getTime() - getTimestampFromFlankValue(a.timestamp).getTime())[0];
  const fallbackDate = reinforcement?.timestamp
    ? getTimestampFromFlankValue(reinforcement.timestamp)
    : sessionId === node.source
      ? getTimestampFromFlankValue(node.createdAt)
      : new Date(0);
  const fallbackTitle = reinforcement?.context?.trim()
    || reinforcement?.quote?.trim()
    || (sessionId === node.source ? node.extractedQuote?.trim() : '')
    || 'Conversation';

  return {
    id: sessionId,
    startedAt: fallbackDate,
    title: fallbackTitle,
  };
}

function getTimestampFromFlankValue(value: FlankTimestamp): Date {
  if (value?.toDate) return value.toDate();
  return new Date(value.seconds * 1000);
}

function formatLinkedSessionDate(date: Date): string {
  if (Number.isNaN(date.getTime()) || date.getTime() <= 0) return 'Date unavailable';
  const dateLabel = date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
  const timeLabel = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${dateLabel} · ${timeLabel}`;
}
